import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Form } from 'antd';
import dayjs from 'dayjs';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import { GenericDataFormContext, LOADING_SAVED_QUOTATIONS_STATES, QuotationInfoContext, QuotationStep } from '../../../../../shared';
import { StepTwoFormContext } from './StepTwoFormContext';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm/WizardDataFormContext';

import { StepTwoForm } from '../../../../../utils';
import { DECISION_TYPE, PERIOD } from '../../../../../../../../../shared/interfaces';

export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [stepTwoForm] = Form.useForm<StepTwoForm>();

  const { setCanShowSpinner } = useContext(GenericDataFormContext);
  const {  
    savedQuotation, 
    loadSaveQuotationState, 
    setLoadSaveQuotationState, 
    setLoadingSavedQuotation
   } = useContext(QuotationInfoContext);
  const { setQuotationForm, quotationForm } = useContext(WizardDataFormContext);

  const [dateFrom, setDateFrom] = useState<string>('');

  const getDateFromLifeObligatory = useCallback(async (todayDate: any) => {
    if (dateFrom) return;

    setCanShowSpinner(true);

    try {
      const response = await QuotationService.getFromDateMandatoryLife(
        todayDate
      );

      const dateFrom = response?.data?.fechaDesde;
      const dateTo = dayjs(dateFrom).add(1, 'year');

      stepTwoForm?.setFieldValue('vigencyDateRange', [dayjs(dateFrom), dateTo]);
      
      setQuotationForm((prevState) => ({
        ...prevState!,
        vigenciaDesde: dayjs(dateFrom).format('YYYY-MM-DD'),
        vigenciaHasta: dayjs(dateTo).format('YYYY-MM-DD'),
      }));

      setDateFrom(dateFrom);
    } catch (error) {} finally {
      setCanShowSpinner(false);
    }
  }, [
    dateFrom,
    setCanShowSpinner,
    stepTwoForm,
    setQuotationForm,
    setDateFrom,
  ]);

  const fillStepTwoServices = useCallback(async () => {
    //** Fill combos */
  }, []);

  const fillStepTwoData = useCallback(async () => {
    const dateFrom = dayjs(savedQuotation.vigenciaDesde, 'DD-MM-YYYY').format('YYYYMMDD');
      const dateTo = dayjs(dateFrom,'YYYYMMDD').add(1, 'year');
      stepTwoForm?.setFieldValue('vigencyDateRange', [dayjs(dateFrom,'YYYYMMDD'), dateTo]);
      setQuotationForm((prevState) => ({
        ...prevState!,
        vigenciaDesde: dayjs(dateFrom,'YYYYMMDD').format('YYYY-MM-DD'),
        vigenciaHasta: dayjs(dateTo).format('YYYY-MM-DD'),
      }));
      setDateFrom(dayjs(dateFrom,'YYYYMMDD').format('YYYY-MM-DD'));

      stepTwoForm.setFields([
        { name: 'quantityInsured', value: savedQuotation.cantidadDeAsegurados },
        { name: 'personalDomestic', value: savedQuotation.esPersonalDomestico },
        { name: 'billingPeriod', value: savedQuotation.esPersonalDomestico === DECISION_TYPE.YES ? PERIOD.YEARLY : PERIOD.MONTHLY },
        { name: 'validityPeriod', value: PERIOD.YEARLY },
        { name: 'vigencyDate', value: dayjs(dateFrom,'YYYYMMDD') },
      ]);

  }, [savedQuotation, stepTwoForm, setDateFrom, setQuotationForm]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING);
      fillStepTwoServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED);
    }
  }, [loadSaveQuotationState]);
  
  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING);
      fillStepTwoData();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING &&
      dateFrom !== '' &&
      quotationForm?.vigenciaDesde !== '' &&
      quotationForm?.vigenciaHasta !== ''
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED);
    }
  }, [loadSaveQuotationState, dateFrom, quotationForm]);

  const values = useMemo(
    () => ({
      stepTwoForm,
      dateFrom,
      setDateFrom,
      getDateFromLifeObligatory,
    }),
    [stepTwoForm, dateFrom, setDateFrom, getDateFromLifeObligatory]
  );

  return (
    <StepTwoFormContext.Provider value={values}>
      {children}
    </StepTwoFormContext.Provider>
  );
};
